import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { useCompany } from "@/common/providers/company-provider";
import { invariant } from "@/common/utils/invariant";

type PostalCodes = z.infer<typeof postalCodesSchema>;

const postalCodesSchema = z.array(
  z.object({
    code: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    name: z.string(),
  }),
);

const fetchData = async (
  { countryCode }: { countryCode: string },
  signal: AbortSignal,
) => {
  const response = await fetch(`/data/postal-codes-${countryCode}.json`, {
    signal,
  });
  const json = await response.json();
  const safeJson = postalCodesSchema.parse(json);

  return safeJson;
};

const usePostalCodesQuery = () => {
  const {
    country: { code },
  } = useCompany();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(code, "countryCode is required");

  return queryOptions({
    queryFn: ({ signal }) =>
      fetchData({ countryCode: code.toLowerCase() }, signal),
    queryKey: ["postal-codes", code],
    select: (data) =>
      data.map(({ code, name, ...rest }) => ({
        ...rest,
        code: code.toString(),
        label: `${code} ${name}`,
        name,
      })),
    staleTime: Infinity,
  });
};

export { usePostalCodesQuery };
export type { PostalCodes };
